/**
 * Inline comment thread (#91) — a GitHub-style thread rendered *inside* a card,
 * used as the management-response field on CUEC + Exception + subservice cards.
 *
 * #113: low-key by default — recorded responses always show, but the composer is
 * hidden behind an "Add comment" button (opens on click, re-collapses on submit).
 * A required-input flow can pass `autoOpen` + `helpText` to pre-open it with
 * guidance (e.g. a partial/none CUEC Mark done, or a subservice-org N/A).
 * #112: an AI-seeded comment (body starts "AI: ") renders as **System** and isn't
 * editable/deletable — it's an automated draft, not the poster's own note.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, SpaceBetween, Textarea } from "@cloudscape-design/components";
import type { Comment } from "@/core/api/types";
import { addComment, deleteComment, editComment, type Kind, listComments } from "@/core/api/comments";
import { formatET } from "./time";

/** #112: AI-seeded comments are stored as "AI: …" authored by the loading user. */
const isAiComment = (c: Comment) => c.body.startsWith("AI:");
const stripAiPrefix = (body: string) => body.replace(/^AI:\s*/, "");

export function CommentThread({
  kind,
  targetId,
  aiSeed,
  onChange,
  onPosted,
  autoOpen = false,
  helpText,
}: {
  kind: Kind;
  targetId: string;
  /** #96: if the thread is empty, post this once as the AI's draft ("AI: …"). */
  aiSeed?: string | null;
  onChange?: () => void;
  /** #117: fired after a NEW management comment is submitted (not edit/delete) —
   *  lets a required-input flow finalize once the rationale exists. */
  onPosted?: () => void;
  /** #113: pre-open the composer (required-input flows). */
  autoOpen?: boolean;
  /** #113: guidance shown above the composer when it's open. */
  helpText?: string;
}) {
  const [items, setItems] = useState<Comment[]>([]);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [composerOpen, setComposerOpen] = useState(autoOpen);
  const seededFor = useRef<string | null>(null);

  // #113: a required-input flow flipping autoOpen pre-opens the composer; backing
  // out of the flow (autoOpen → false) re-collapses it so the abandoned composer
  // doesn't linger. Manual opens are unaffected — this only fires on autoOpen flips.
  useEffect(() => {
    setComposerOpen(autoOpen);
  }, [autoOpen]);

  const refresh = useCallback(async () => {
    setItems(await listComments(kind, targetId));
  }, [kind, targetId]);

  const load = useCallback(async () => {
    const list = await listComments(kind, targetId);
    const seed = (aiSeed ?? "").trim();
    if (list.length === 0 && seed && seededFor.current !== targetId) {
      seededFor.current = targetId; // guard against double-seed this mount
      await addComment(kind, targetId, `AI: ${seed}`);
      setItems(await listComments(kind, targetId));
      onChange?.();
    } else {
      setItems(list);
    }
  }, [kind, targetId, aiSeed, onChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const post = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await addComment(kind, targetId, body);
      setDraft("");
      setComposerOpen(false); // #113: re-collapse after submitting
      await refresh();
      onChange?.();
      onPosted?.(); // #117: a rationale now exists — finalize the gated decision
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: string) => {
    const body = editDraft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await editComment(kind, id, body);
      setEditingId(null);
      await refresh();
      onChange?.();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await deleteComment(kind, id);
      await refresh();
      onChange?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <SpaceBetween size="xs">
      {items.length === 0 && (
        <Box variant="small" color="text-body-secondary">
          No comments yet.
        </Box>
      )}
      {items.map((c) => {
        const ai = isAiComment(c);
        return (
          <Box key={c.id} padding={{ top: "xxs" }}>
            <Box variant="small" color="text-body-secondary">
              {ai ? "System" : (c.user_email ?? "Management")} · {formatET(c.created_at)}
            </Box>
            {editingId === c.id ? (
              <SpaceBetween size="xxs">
                <Textarea
                  value={editDraft}
                  onChange={({ detail }) => setEditDraft(detail.value)}
                  rows={3}
                  disabled={busy}
                />
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    onClick={() => void saveEdit(c.id)}
                    disabled={busy}
                    loading={busy}
                  >
                    Save
                  </Button>
                  <Button variant="inline-link" onClick={() => setEditingId(null)} disabled={busy}>
                    Cancel
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <span style={{ whiteSpace: "pre-wrap" }}>{ai ? stripAiPrefix(c.body) : c.body}</span>
                {/* #122: management owns the workpaper, so the AI draft (System) is
                    editable. Editing shows it without the "AI:" prefix and saves it
                    back without one — so it becomes the user's own comment (re-
                    attributed to them, and counts as documented rationale, so no
                    separate comment is required). */}
                {c.editable && (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      variant="inline-link"
                      onClick={() => {
                        setEditingId(c.id);
                        setEditDraft(ai ? stripAiPrefix(c.body) : c.body);
                      }}
                      disabled={busy}
                    >
                      Edit
                    </Button>
                    <Button variant="inline-link" onClick={() => void remove(c.id)} disabled={busy}>
                      Delete
                    </Button>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            )}
          </Box>
        );
      })}

      {/* #113: composer hidden behind "Add comment" until clicked (or auto-opened). */}
      {composerOpen ? (
        <SpaceBetween size="xxs">
          {helpText && (
            <Box variant="small" color="text-body-secondary">
              {helpText}
            </Box>
          )}
          <Textarea
            value={draft}
            onChange={({ detail }) => setDraft(detail.value)}
            rows={3}
            placeholder="Add your response…"
            disabled={busy}
          />
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              onClick={() => void post()}
              disabled={busy || !draft.trim()}
              loading={busy}
            >
              Submit
            </Button>
            {!autoOpen && (
              <Button
                variant="inline-link"
                onClick={() => {
                  setDraft("");
                  setComposerOpen(false);
                }}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      ) : (
        <Box>
          <Button iconName="add-plus" onClick={() => setComposerOpen(true)} disabled={busy}>
            Add comment
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
}
